import { effectiveShiftMinutes, madridClockTime, MAX_ACTIVE_SHIFT_MINUTES } from "../../../../shared/shiftDuration";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Clock, Pause, Play, Square, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function formatShiftDate(date: string) {
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
}

function formatShiftStopTime(value: Date | string | null | undefined) {
    if (!value) return "hora desconocida";
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? "hora desconocida" : madridClockTime(parsed).slice(0, 5);
}

export function ShiftControl() {
    const utils = trpc.useUtils();
    const { data: shift, isLoading } = trpc.shifts.getToday.useQuery(undefined, { refetchInterval: 15000 });
    const invalidateTechnicianAssignments = utils.dailyAssignments.listForTechnician.invalidate;
    const [, setLocation] = useLocation();
    const [clockNow, setClockNow] = useState(() => new Date());
    const [vehicleChoice, setVehicleChoice] = useState<"vehicle" | "none" | null>(null);
    const [vehicleRegistration, setVehicleRegistration] = useState("");

    useEffect(() => {
        const timer = setInterval(() => setClockNow(new Date()), 15000);
        return () => clearInterval(timer);
    }, []);

    // getToday polls for scheduler/rollover pauses too. Refresh execution rows
    // whenever the observed shift is paused so active activity timers stop.
    useEffect(() => {
        if (shift?.status === "paused") void invalidateTechnicianAssignments();
    }, [shift?.status, shift?.date, invalidateTechnicianAssignments]);

    // State for compliance validation
    const [isCheckingCompliance, setIsCheckingCompliance] = useState(false);
    const [showComplianceModal, setShowComplianceModal] = useState(false);
    const [showConfirmEndModal, setShowConfirmEndModal] = useState(false);
    const [confirmShiftId, setConfirmShiftId] = useState<number | undefined>();
    const [complianceData, setComplianceData] = useState<{
        totalMinutes: number;
        reportOutstanding?: boolean;
        date?: string;
        shiftId?: number;
        pendingReports?: Array<{ date: string; installationId: number; installationName: string; totalMinutes: number | null }>;
    } | null>(null);

    const canEndQuery = trpc.shifts.canEnd.useQuery(undefined, { enabled: false });
    const pendingReportsQuery = trpc.shifts.pendingReports.useQuery(undefined, { refetchInterval: 15000 });
    const pendingFinalizationQuery = trpc.shifts.pendingFinalization.useQuery(undefined, { refetchInterval: 15000 });

    const fetchCanEnd = async (shiftId?: number) => shiftId !== undefined
        ? utils.shifts.canEnd.fetch({ shiftId })
        : (await canEndQuery.refetch()).data;

    const handleEndClick = async (shiftId?: number) => {
        setIsCheckingCompliance(true);
        try {
            const result = await fetchCanEnd(shiftId);
            if (result) {
                setComplianceData({
                    totalMinutes: result.totalMinutes,
                    reportOutstanding: result.reportOutstanding,
                    date: result.date,
                    shiftId,
                    pendingReports: result.pendingReports,
                });
                if (result.reportOutstanding && result.allowed) {
                    setShowComplianceModal(true);
                } else if (result.allowed) {
                    setConfirmShiftId(shiftId);
                    setShowConfirmEndModal(true);
                } else {
                    toast.error("No se pudo validar el cierre de jornada");
                }
            }
        } catch (err) {
            toast.error("Error al validar requisitos de cierre");
        } finally {
            setIsCheckingCompliance(false);
        }
    };

    const startMutation = trpc.shifts.start.useMutation({
        onSuccess: () => {
            toast.success("Jornada iniciada");
            setVehicleChoice(null);
            setVehicleRegistration("");
            utils.shifts.getToday.invalidate();
        },
        onError: (err) => toast.error(err.message)
    });

    const handleStart = () => {
        if (vehicleChoice === "none") {
            startMutation.mutate({ vehicleNotUsed: true });
        } else if (vehicleChoice === "vehicle" && vehicleRegistration.trim()) {
            startMutation.mutate({ vehicleNotUsed: false, vehicleRegistration: vehicleRegistration.trim() });
        }
    };

    const pauseMutation = trpc.shifts.pause.useMutation({
        onSuccess: () => {
            toast.success("Jornada pausada");
            utils.shifts.getToday.invalidate();
            utils.dailyAssignments.listForTechnician.invalidate();
        },
        onError: (err) => toast.error(err.message)
    });

    const resumeMutation = trpc.shifts.resume.useMutation({
        onSuccess: () => {
            toast.success("Jornada reanudada");
            utils.shifts.getToday.invalidate();
        },
        onError: (err) => toast.error(err.message)
    });

    const endMutation = trpc.shifts.end.useMutation({
        onSuccess: (result: any) => {
            if (result.reportOutstanding) {
                toast.info("Jornada finalizada. Los partes siguen pendientes; el cierre no modifica ni completa la instalación.");
            } else {
                toast.success("Jornada finalizada");
            }
            setShowConfirmEndModal(false);
            setShowComplianceModal(false);
            utils.shifts.getToday.invalidate();
            utils.shifts.pendingReports.invalidate();
            utils.shifts.pendingFinalization.invalidate();
            utils.dailyAssignments.listForTechnician.invalidate();
        },
        onError: (err) => {
            if (err.data?.httpStatus === 409) {
                setShowComplianceModal(true);
            } else {
                toast.error(err.message);
            }
        }
    });

    const handleFinalizeWithDebt = async () => {
        setIsCheckingCompliance(true);
        try {
            const refreshed = await fetchCanEnd(complianceData?.shiftId);
            if (!refreshed?.allowed) {
                toast.error("No se pudo validar el cierre de jornada");
                return;
            }
            setComplianceData({
                totalMinutes: refreshed.totalMinutes,
                reportOutstanding: refreshed.reportOutstanding,
                date: refreshed.date,
                shiftId: complianceData?.shiftId,
                pendingReports: refreshed.pendingReports,
            });
            setShowComplianceModal(false);
            endMutation.mutate(complianceData?.shiftId === undefined ? undefined : { shiftId: complianceData.shiftId });
        } catch {
            toast.error("Error al revalidar los partes pendientes");
        } finally {
            setIsCheckingCompliance(false);
        }
    };

    if (isLoading) return <div className="h-20 animate-pulse bg-muted rounded-lg" />;

    const status = shift?.status;
    const totalMinutes = shift ? effectiveShiftMinutes(shift, clockNow) : 0;
    const hasPendingForCurrentDate = Boolean(shift && pendingReportsQuery.data?.some((pending: any) => pending.date === shift.date));

    return (
        <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${status === 'active' ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                        <Clock className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">
                            {!shift ? "Sin jornada" :
                                status === 'active' ? "Jornada Activa" :
                                    status === 'paused' ? "En Pausa" : "Finalizada"}
                        </h3>
                        {shift?.activeStartAt && status === 'active' && (
                            <p className="text-xs text-muted-foreground">Inició a las {madridClockTime(new Date(shift.activeStartAt)).slice(0, 5)}</p>
                        )}
                        {shift && (
                            <p className="text-xs text-muted-foreground">
                                Vehículo: {shift.vehicleNotUsed === true
                                    ? "Sin vehículo"
                                    : typeof shift.vehicleRegistration === "string" && shift.vehicleRegistration.trim()
                                        ? shift.vehicleRegistration.trim()
                                        : "Sin registrar"}
                            </p>
                        )}
                        {totalMinutes > 0 && (
                            <p className="text-xs text-muted-foreground">Total jornada: {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {!shift && (
                        <div className="flex flex-wrap items-end gap-3">
                            <fieldset className="flex flex-col gap-1 text-xs">
                                <legend className="mb-1 font-medium">Vehículo de hoy</legend>
                                <label className="flex items-center gap-1.5">
                                    <input
                                        type="radio"
                                        name="shift-vehicle-choice"
                                        value="vehicle"
                                        checked={vehicleChoice === "vehicle"}
                                        onChange={() => setVehicleChoice("vehicle")}
                                    />
                                    Con vehículo
                                </label>
                                <label className="flex items-center gap-1.5">
                                    <input
                                        type="radio"
                                        name="shift-vehicle-choice"
                                        value="none"
                                        checked={vehicleChoice === "none"}
                                        onChange={() => setVehicleChoice("none")}
                                    />
                                    Sin vehículo
                                </label>
                            </fieldset>
                            {vehicleChoice === "vehicle" && (
                                <div className="w-44">
                                    <label htmlFor="shift-vehicle-registration" className="mb-1 block text-xs font-medium">
                                        Matrícula / vehículo
                                    </label>
                                    <Input
                                        id="shift-vehicle-registration"
                                        value={vehicleRegistration}
                                        onChange={(event) => setVehicleRegistration(event.target.value)}
                                        maxLength={64}
                                        autoComplete="off"
                                        placeholder="Matrícula o identificador"
                                    />
                                </div>
                            )}
                            <Button
                                onClick={handleStart}
                                disabled={!vehicleChoice || (vehicleChoice === "vehicle" && !vehicleRegistration.trim()) || startMutation.isPending}
                                size="lg"
                                className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                            >
                                <Play className="w-4 h-4" /> Iniciar
                            </Button>
                        </div>
                    )}

                    {status === 'active' && (
                        <>
                            <Button onClick={() => pauseMutation.mutate()} variant="outline" size="icon" title="Pausar">
                                <Pause className="w-5 h-5 text-yellow-600" />
                            </Button>

                            <Button
                                onClick={() => handleEndClick()}
                                disabled={isCheckingCompliance || endMutation.isPending}
                                variant="destructive"
                                size="icon"
                                title="Finalizar Jornada"
                            >
                                {isCheckingCompliance ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Square className="w-5 h-5 fill-current" />
                                )}
                            </Button>
                        </>
                    )}

                    {status === 'paused' && (
                        <>
                            {totalMinutes < MAX_ACTIVE_SHIFT_MINUTES && (
                                <Button onClick={() => resumeMutation.mutate()} size="lg" className="gap-2">
                                    <Play className="w-4 h-4" /> Reanudar
                                </Button>
                            )}
                            <Button onClick={() => handleEndClick()} disabled={isCheckingCompliance || endMutation.isPending} variant="destructive">
                                Finalizar jornada
                            </Button>
                        </>
                    )}

                    {status === 'ended' && (
                        <Button disabled variant="outline">Finalizada</Button>
                    )}
                </div>
            </CardContent>

            {status === 'paused' && totalMinutes >= MAX_ACTIVE_SHIFT_MINUTES && (
                <div className="mx-4 mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
                    <strong>{hasPendingForCurrentDate ? "Jornada pausada. Parte pendiente de completar." : "Jornada pausada automáticamente al alcanzar 490 minutos."}</strong>
                    <p className="mt-1">El contador no seguirá avanzando. No se reanudará automáticamente.</p>
                </div>
            )}

            {pendingFinalizationQuery.data?.map((prior: any) => {
                const total = Math.max(0, prior.totalMinutes ?? 0);
                const shortDate = formatShiftDate(prior.date).slice(0, 5);
                return (
                    <div key={prior.id} className="mx-4 mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
                        <p>
                            <strong>Jornada del {formatShiftDate(prior.date)} pausada · {Math.floor(total / 60)}h {String(total % 60).padStart(2, "0")}m · detenida a las {formatShiftStopTime(prior.endAt)} (Madrid). No suma tiempo. Finalízala para cerrar sus actividades.</strong>
                        </p>
                        <p className="mt-1">{prior.startedActivities} {prior.startedActivities === 1 ? "actividad iniciada" : "actividades iniciadas"} pendientes de cierre.</p>
                        <Button
                            className="mt-2 border-amber-500 bg-white"
                            variant="outline"
                            size="sm"
                            disabled={isCheckingCompliance || endMutation.isPending}
                            onClick={() => handleEndClick(prior.id)}
                        >
                            Finalizar jornada del {shortDate}
                        </Button>
                    </div>
                );
            })}

            {pendingReportsQuery.data?.length ? (
                <div className="mx-4 mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
                    <p className="mb-2"><strong>{pendingReportsQuery.data.length} {pendingReportsQuery.data.length === 1 ? "parte pendiente" : "partes pendientes"}</strong>. Puedes fichar la jornada de hoy; registra cada parte con la fecha del trabajo.</p>
                    <div className="space-y-2">
                        {pendingReportsQuery.data.map((pending: any) => (
                            <div key={`${pending.installationId}-${pending.date}`} className="flex flex-wrap items-center justify-between gap-3">
                                <p>{pending.date} · {pending.installationName} · #{pending.installationId} · {pending.totalMinutes === null ? "Duración no verificada" : `${Math.floor(pending.totalMinutes / 60)}h ${String(pending.totalMinutes % 60).padStart(2, "0")}m`}</p>
                                <Button variant="outline" size="sm" className="border-amber-500 bg-white"
                                    onClick={() => setLocation(`/daily-reports/new?installationId=${pending.installationId}&date=${pending.date}${status === 'paused' && pending.date === shift?.date ? "&from=shift_end" : ""}`)}>
                                    <FileText className="mr-2 h-4 w-4" /> Registrar parte
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Modal: Confirm End Shift (Pro-checked) */}
            <AlertDialog open={showConfirmEndModal} onOpenChange={setShowConfirmEndModal}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Finalizar Jornada?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Se cerrará el fichaje y se conservarán las horas reales registradas. Si necesitas continuar trabajando hoy, no finalices todavía.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={endMutation.isPending}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                endMutation.mutate(confirmShiftId === undefined ? undefined : { shiftId: confirmShiftId });
                            }}
                            disabled={endMutation.isPending}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {endMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Finalizar jornada{confirmShiftId !== undefined ? ` del ${formatShiftDate(complianceData?.date ?? "").slice(0, 5)}` : ""}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Explicit close confirmation when documentary debt remains open. */}
            <AlertDialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
                <AlertDialogContent className="border-amber-200">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
                            <FileText className="w-5 h-5" />
                            {complianceData?.pendingReports?.length === 1
                                ? "Hay un parte diario pendiente"
                                : "Hay partes diarios pendientes"}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-foreground/80">
                            Puedes finalizar la jornada sin registrar estos partes. La deuda documental seguirá abierta; el cierre no completa ni cierra la instalación:
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2 rounded-md bg-amber-50 p-3 text-sm text-amber-950">
                        {complianceData?.pendingReports?.map((pending) => (
                            <div key={`${pending.installationId}-${pending.date}`}>
                                <p><strong>{pending.date}</strong> · {pending.installationName} · #{pending.installationId} · {pending.totalMinutes === null ? "Duración no verificada" : `${Math.floor(pending.totalMinutes / 60)}h ${String(pending.totalMinutes % 60).padStart(2, "0")}m`}</p>
                                <Button
                                    type="button"
                                    variant="link"
                                    className="h-auto p-0 text-amber-900 underline"
                                    onClick={() => {
                                        const shiftParam = complianceData.shiftId === undefined ? "" : `&shiftId=${complianceData.shiftId}`;
                                        setShowComplianceModal(false);
                                        setLocation(`/daily-reports/new?installationId=${pending.installationId}&date=${pending.date}&from=shift_end${shiftParam}`);
                                    }}
                                >
                                    Registrar parte del {formatShiftDate(pending.date)}
                                </Button>
                            </div>
                        ))}
                    </div>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel className="sm:flex-1" disabled={isCheckingCompliance || endMutation.isPending}>Cerrar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(event) => {
                                event.preventDefault();
                                void handleFinalizeWithDebt();
                            }}
                            disabled={isCheckingCompliance || endMutation.isPending}
                            className="bg-primary sm:flex-[2] gap-2"
                        >
                            {isCheckingCompliance ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            Finalizar jornada dejando partes pendientes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
